import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

/**
 * The frame a member's panel opens in from the Members table (their Capability,
 * their Activity, their Elliott Conversations): the learner dashboard's panel
 * frame, with whose it is on a ruled header and the panel scrolling beneath.
 * `flush` hands the whole body to a panel that scrolls itself.
 */
const MemberPanelDialog: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  flush?: boolean;
  children: React.ReactNode;
}> = ({ open, title, onClose, width = 560, flush = false, children }) => {
  // A phone: the panel takes the whole screen, under its header and close.
  const isMobileApp = useIsMobileApp();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobileApp}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.60)', backdropFilter: 'blur(10.95px)' } } }}
      PaperProps={{
        sx: {
          width,
          maxWidth: '95vw',
          height: 'min(820px, 90vh)',
          display: 'flex',
          flexDirection: 'column',
          // The learner dashboard's panel: its surface, brandy frame and glow.
          bgcolor: '#1C1C1C',
          backgroundImage: 'none',
          borderRadius: '8px',
          border: '1px solid rgba(247, 148, 32, 0.45)',
          overflow: 'hidden',
          boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
          [MOBILE_SX]: { width: '100%', maxWidth: '100%', height: '100%', m: 0, border: 'none', borderRadius: 0 }
        }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          pl: '25px',
          pr: '45px',
          py: '20px',
          borderBottom: '1px solid #2A2A2A',
          position: 'relative',
          [MOBILE_SX]: { pl: '16px', pr: '52px', py: '14px' }
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 18,
            fontWeight: 700,
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif'
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0.5,
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            [MOBILE_SX]: { p: '10px', right: 6 }
          }}
        >
          <CloseIcon sx={{ fontSize: 20, color: '#FDFDFD' }} />
        </IconButton>
      </Box>
      {flush ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#565656 transparent'
          }}
        >
          {children}
        </Box>
      )}
    </Dialog>
  );
};

export default MemberPanelDialog;
